import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations } from '../i18n/translations';
import { vehicles as mockVehicles } from '../mock/mock';
import { addKeywords } from '../mock/seoKeywords';

const AppContext = createContext(null);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PRICING = { BASE: 30, PER_KM: 2.4, MIN: 60, HOURLY: 95, NIGHT_PCT: 0, NIGHT_FROM: 22, NIGHT_TO: 6, CITY_BASE: 30, CITY_PER_KM: 2.4, CITY_MIN: 60 };

export const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  if (src.startsWith('/api/')) return `${BACKEND}${src}`;
  return src;
};

const UI_LANGS = ['de', 'en', 'fr', 'it'];

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => { const l = localStorage.getItem('att_lang') || 'de'; return UI_LANGS.includes(l) ? l : 'en'; });
  const [contentLang, setContentLang] = useState(() => localStorage.getItem('att_content_lang') || localStorage.getItem('att_lang') || 'de');
  const [currency, setCurrency] = useState(() => localStorage.getItem('att_currency') || 'CHF');
  const [vehicles, setVehicles] = useState(mockVehicles);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [routePrices, setRoutePrices] = useState([]);
  const [extras, setExtras] = useState({ enabled: true, title: 'Zurich Surprise', items: [] });
  const [waText, setWaText] = useState('');

  const [kwReady, setKwReady] = useState(false);
  const [kwVersion, setKwVersion] = useState(0);
  useEffect(() => {
    fetch(`${BACKEND}/api/keywords`).then((r) => r.json()).then((d) => { setKwVersion(addKeywords(d)); }).catch(() => {}).finally(() => setKwReady(true));
  }, []);
  useEffect(() => {
    fetch(`${BACKEND}/api/vehicles`).then((r) => r.json()).then((d) => {
      if (Array.isArray(d) && d.length) setVehicles(d);
    }).catch(() => {});
    fetch(`${BACKEND}/api/config/pricing`).then((r) => r.json()).then((d) => {
      if (d && d.BASE != null) setPricing({ ...DEFAULT_PRICING, ...d });
    }).catch(() => {});
    fetch(`${BACKEND}/api/route-prices`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setRoutePrices(d); }).catch(() => {});
    fetch(`${BACKEND}/api/extras`).then((r) => r.json()).then((d) => { if (d && Array.isArray(d.items)) setExtras(d); }).catch(() => {});
  }, []);

  const changeLang = useCallback((l) => {
    const ui = UI_LANGS.includes(l) ? l : 'en';
    document.documentElement.dir = ['ar', 'he', 'fa', 'ku'].includes(l) ? 'rtl' : 'ltr';
    document.documentElement.lang = l;
    setLang(ui); localStorage.setItem('att_lang', ui);
    setContentLang(l); localStorage.setItem('att_content_lang', l);
  }, []);
  const changeCurrency = useCallback((c) => { setCurrency(c); localStorage.setItem('att_currency', c); }, []);

  const priceFor = useCallback((km, factorOrVehicle = 1, zoneOrPerKm = 'airport') => {
    if (!km || km <= 0) return null;
    const v = typeof factorOrVehicle === 'object' && factorOrVehicle ? factorOrVehicle : { priceFactor: factorOrVehicle, perKm: typeof zoneOrPerKm === 'number' ? zoneOrPerKm : null };
    const city = zoneOrPerKm === 'city';
    const f = Number(v.priceFactor) || 1;
    const rate = city ? (Number(v.perKmCity) || pricing.CITY_PER_KM * f) : (Number(v.perKm) || pricing.PER_KM * f);
    const base = city ? pricing.CITY_BASE * f : (Number(v.baseFare) || pricing.BASE * f);
    const min = city ? pricing.CITY_MIN * f : (Number(v.minFare) || pricing.MIN * f);
    return Math.round(Math.max(min, base + rate * km));
  }, [pricing]);

  const t = useCallback((path) => {
    const parts = path.split('.');
    const walk = (root) => { let cur = root; for (const p of parts) { if (cur == null) return undefined; cur = cur[p]; } return cur; };
    const v = walk(translations[contentLang]) ?? walk(translations[lang]);
    if (v != null) return v;
    const en = walk(translations.en);
    return en == null ? path : en;
  }, [lang, contentLang]);

  return (
    <AppContext.Provider value={{ lang, contentLang, changeLang, currency, changeCurrency, t, vehicles, pricing, routePrices, extras, waText, setWaText, priceFor, kwReady, kwVersion }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
